import {
  ActionIcon,
  Group,
  Menu,
  Table,
  Text,
  Tooltip,
  Modal,
  TextInput,
  NumberInput,
  Button,
  Stack,
} from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { IconDots, IconPencil, IconTrashX, IconEye, IconRotate } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  IntegrationRule,
  SECONDS_PER_DAY,
  SECONDS_PER_HOUR,
  UpdateIntegrationRuledata,
  deleteIntegrationRule,
  syncIntegrationRule,
  updateIntegrationRule,
  getDatasetsFilesType,
} from '@mygpt/app';
import { disassociateGbaseProject } from '@mygpt/app/apis';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useParams, useNavigate } from 'react-router-dom';
import { showNotification } from '@mantine/notifications';
import { useIntegrationRuleStore } from '@/pages/Datasets/Integration/Tasks/useIntegrationRuleStore';
import { useDatasetStore } from '@/store/dataset';

interface Props {
  item: IntegrationRule;
  botId?: string;
}

export const ListItem = ({ item, botId }: Props) => {
  const { t, i18n } = useTranslation();
  const { datasetId: routeDatasetId } = useParams<{ datasetId: string }>();
  const [opened, handlers] = useDisclosure(false);
  const navigate = useNavigate();
  const { updateRuleByID, removeRuleByID, fetchRules } = useIntegrationRuleStore();
  const { init: initFilesInfo } = useDatasetStore();

  const form = useForm<UpdateIntegrationRuledata>({
    initialValues: {
      name: item.name,
      sync_interval: Math.max(1, Math.floor(item.sync_interval / SECONDS_PER_DAY)),
    },
    validate: {
      name: (value) => (value ? null : t('create_v2.integrations.task_management.validation.name_required')),
      sync_interval: (value) =>
        Number(value) >= 0 ? null : t('create_v2.integrations.task_management.validation.sync_interval_required'),
    },
  });

  const freshDatasetCounts = async () => {
    if (!routeDatasetId) return;
    try {
      const res = await getDatasetsFilesType({ id: routeDatasetId, language: i18n.language });
      initFilesInfo(res.data);
    } catch (err) {
      console.log('获取知识库数据的数量数据失败：', err);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!routeDatasetId) return;
    try {
      const sync_interval = values.sync_interval <= 0 ? 1 : values.sync_interval;
      const data = {
        name: values.name,
        sync_interval: sync_interval * SECONDS_PER_DAY, // 从网页上的天单位转换为秒,
      };
      await updateIntegrationRule(routeDatasetId, item.id, data);
      updateRuleByID(item.id, data);

      handlers.close();
    } catch (error) {
      console.error('Failed to update integration rule:', error);
    }
  };

  const deleteFileConfirm = () => {
    if (!routeDatasetId) return;

    openConfirmModal({
      title: t('create_v2.integrations.task_management.delete_task'),
      centered: true,
      children: (
        <Text className="break-all">
          {t('create_v2.integrations.task_management.delete_task_confirm', { name: item.name })}
        </Text>
      ),
      labels: { confirm: t('delete_btn'), cancel: t('alert_cancel') },
      onCancel: () => {},
      confirmProps: {
        color: 'red',
        onClick: async () => {
          // 判断是 GBase Project 还是 Lark 集成
          if (item.type === 'gbase_project' && routeDatasetId) {
            // 解除 GBase Project 关联
            await disassociateGbaseProject(routeDatasetId, item.id);
          } else {
            // 删除 Lark 集成
            await deleteIntegrationRule(routeDatasetId, item.id);
          }

          showNotification({
            message: t('create_v2.integrations.task_management.delete_task_success'),
            color: 'green',
          });
          removeRuleByID(item.id);
          fetchRules({ datasetId: routeDatasetId, botId });
          freshDatasetCounts();
        },
      },
    });
  };

  const navigateFiles = () => {
    navigate(`${item.id}`);
  };

  const manualSync = async () => {
    if (!routeDatasetId) return;
    try {
      await syncIntegrationRule(routeDatasetId, item.id);
      showNotification({
        message: t('create_v2.integrations.task_management.manual_sync_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to sync integration rule:', error);
    }
  };

  const handleManualSync = () => {
    if (!routeDatasetId) return;

    openConfirmModal({
      title: t('create_v2.integrations.task_management.manual_sync'),
      centered: true,
      children: <Text>{t('create_v2.integrations.task_management.manual_sync_confirm', { name: item.name })}</Text>,
      labels: { confirm: t('alert_ok'), cancel: t('alert_cancel') },
      onCancel: () => {},
      confirmProps: {
        onClick: manualSync,
      },
    });
  };

  // sync_interval 单位为秒，需要转换为天或小时展示
  // 展示同步间隔，如果大于等于1天，则展示天 N 天，否则展示小时单位 N 小时
  const showSyncInterval = (sync_interval: number) => {
    const day = sync_interval / SECONDS_PER_DAY;
    const hour = sync_interval / SECONDS_PER_HOUR;

    if (day >= 1) {
      return `${day} ${t('create_v2.integrations.task_management.sync_interval_unit_day')}`;
    }
    return `${hour} ${t('create_v2.integrations.task_management.sync_interval_unit_hour')}`;
  };

  return (
    <>
      <Table.Tr>
        <Table.Td className="max-w-[100px]">
          <Tooltip label={item.name} multiline>
            <Text size="sm" className="truncate">
              {item.name || '-'}
            </Text>
          </Tooltip>
        </Table.Td>
        <Table.Td className="max-w-[250px]">
          <Text size="sm">
            {item.detail.share_urls.map((url) => (
              <Tooltip label={url.url} multiline>
                <Text key={url.url} className="truncate">
                  {url.url}
                </Text>
              </Tooltip>
            ))}
          </Text>
        </Table.Td>
        <Table.Td className="max-w-[100px]">
          <Text truncate size="sm">
            {item.created_by?.name || '-'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{new Date(item.created_at).toLocaleString()}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{item.file_count || '-'}</Text>
        </Table.Td>
        <Table.Td>
          {/* GBase Project 不显示状态 */}
          {item.type === 'gbase_project' ? (
            <Text size="sm">-</Text>
          ) : (
            <Stack gap={4}>
              <Group gap={4}>
                <Text size="sm" c="gray.6">
                  {t('embeddings.table.filter_by_status.learned')}：
                </Text>
                <Text size="sm">{item.status?.complete || '-'}</Text>
              </Group>
              <Group gap={4}>
                <Text size="sm" c="gray.6">
                  {t('embeddings.table.filter_by_status.learning')}：
                </Text>
                <Text size="sm">{item.status?.process || '-'}</Text>
              </Group>
              <Group gap={4}>
                <Text size="sm" c="gray.6">
                  {t('embeddings.table.filter_by_status.ready')}：
                </Text>
                <Text size="sm">{item.status?.ready || '-'}</Text>
              </Group>
              <Group gap={4}>
                <Text size="sm" c="gray.6">
                  {t('embeddings.table.filter_by_status.failed')}：
                </Text>
                <Text size="sm">{(item.status?.fail || 0) + (item.status?.exceeded || 0) || '-'}</Text>
              </Group>
              <Group gap={4}>
                <Text size="sm" c="gray.6">
                  {t('embeddings.table.filter_by_status.unavailable')}：
                </Text>
                <Text size="sm">{item.status?.invalid || '-'}</Text>
              </Group>
            </Stack>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{item.last_synced_at ? new Date(item.last_synced_at).toLocaleString() : '-'}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{showSyncInterval(item.sync_interval)}</Text>
        </Table.Td>
        <Table.Td>
          <Group gap={5} wrap="nowrap" justify="center">
            {/* 如果是 GBase Project，只显示删除按钮 */}
            {item.type !== 'gbase_project' && (
              <Menu>
                <Menu.Target>
                  <ActionIcon variant="subtle">
                    <IconDots size="1rem" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconEye size={14} />} onClick={navigateFiles}>
                    {t('create_v2.integrations.task_management.view_files')}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconPencil size={14} />} onClick={handlers.open}>
                    {t('edit')}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconRotate size={14} />} onClick={handleManualSync}>
                    {t('create_v2.integrations.task_management.manual_sync')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
            <ActionIcon variant="subtle" color="red" onClick={deleteFileConfirm}>
              <IconTrashX size={14} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>

      <Modal opened={opened} onClose={handlers.close} title={t('edit')} centered>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            label={t('create_v2.integrations.task_management.name')}
            placeholder={t('create_v2.integrations.task_management.name_placeholder')}
            {...form.getInputProps('name')}
            mb="md"
          />
          <NumberInput
            label={t('create_v2.integrations.task_management.sync_interval_modal_label')}
            placeholder={t('create_v2.integrations.task_management.sync_interval_placeholder')}
            {...form.getInputProps('sync_interval')}
            mb="xl"
            min={1}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={handlers.close}>
              {t('alert_cancel')}
            </Button>
            <Button type="submit">{t('alert_ok')}</Button>
          </Group>
        </form>
      </Modal>
    </>
  );
};
